import { useState, useEffect } from 'react';
import AnimatedContent from '../components/AnimatedContent';
import { UserPlus, Settings } from 'lucide-react';
import { colors } from '../styles/colors';
import { useUsersManagement } from '../hooks/useUsersManagement';
import { useToast } from '../contexts/ToastContext';
import UsersFilters from '../components/users/UsersFilters';
import UsersTable from '../components/users/UsersTable';
import UserFormModal from '../components/users/UserFormModal';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import UpdatePhotoModal from '../components/UpdatePhotoModal';
import PrestatairesTab from '../components/referentiels/PrestatairesTab';
import ClientsTab from '../components/referentiels/ClientsTab';
import ProjetsTab from '../components/referentiels/ProjetsTab';
import ConsultantsTab from '../components/referentiels/ConsultantsTab';

// --- Onglets de navigation ---

const TABS = [
  { id: 'utilisateurs',  label: 'Utilisateurs' },
  { id: 'prestataires',  label: 'Prestataires porteurs' },
  { id: 'clients',       label: 'Clients finaux' },
  { id: 'projets',       label: 'Projets' },
  { id: 'consultants',   label: 'Consultants' },
];

function TabBar({ activeTab, onTabChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: `2px solid ${colors.secondary.lightGray}`, marginBottom: '1.5rem', gap: '0' }}>
      {TABS.map(tab => {
        const active = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              padding: '0.75rem 1.25rem',
              background: 'none',
              border: 'none',
              borderBottom: active ? `3px solid ${colors.primary.teal}` : '3px solid transparent',
              marginBottom: '-2px',
              fontSize: '0.9375rem',
              fontWeight: active ? '600' : '400',
              color: active ? colors.primary.teal : colors.secondary.mediumGray,
              cursor: 'pointer',
              fontFamily: '"Noto Sans", sans-serif',
              transition: 'color 150ms, border-color 150ms',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = colors.primary.gray; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = colors.secondary.mediumGray; }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// --- Contenu onglet Utilisateurs ---

function UsersTabContent() {
  const {
    consultants, loading, error, filteredUsers,
    filterRole, setFilterRole,
    filterActif, setFilterActif,
    searchTerm, setSearchTerm,
    showModal, modalMode, submitting, formData, setFormData,
    handleOpenModal, handleCloseModal, handleSubmit,
    showDeleteModal, userToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    showPhotoModal, userForPhoto,
    handleOpenPhotoModal, handlePhotoUpdate,
    setShowPhotoModal, setUserForPhoto,
    handleForcePasswordChange,
  } = useUsersManagement();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div style={{ width: '40px', height: '40px', border: `4px solid ${colors.secondary.lightTeal}`, borderTop: `4px solid ${colors.primary.teal}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <span style={{ fontFamily: '"Noto Sans", sans-serif', color: colors.secondary.mediumGray, fontSize: '0.9375rem' }}>
          Gestion des comptes utilisateurs
        </span>
        <button
          onClick={() => handleOpenModal('create')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: colors.primary.teal, color: colors.text.white,
            border: 'none', borderRadius: '0.5rem',
            fontSize: '1rem', fontWeight: '500', cursor: 'pointer',
            transition: 'all 200ms ease-in-out',
            fontFamily: '"Noto Sans", sans-serif',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005f6a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary.teal}
        >
          <UserPlus size={20} />
          Ajouter un utilisateur
        </button>
      </div>

      <UsersFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterRole={filterRole}
        onRoleChange={setFilterRole}
        filterActif={filterActif}
        onActifChange={setFilterActif}
      />

      {error && (
        <div style={{ backgroundColor: colors.background.lightRed, border: `1px solid ${colors.status.error}`, color: colors.text.darkRed, padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontFamily: '"Noto Sans", sans-serif' }}>
          {error}
        </div>
      )}

      <UsersTable
        filteredUsers={filteredUsers}
        consultants={consultants}
        onEdit={(user) => handleOpenModal('edit', user)}
        onDelete={handleDeleteClick}
        onPhoto={handleOpenPhotoModal}
        onForcePassword={handleForcePasswordChange}
      />

      <UserFormModal
        isOpen={showModal}
        mode={modalMode}
        formData={formData}
        setFormData={setFormData}
        consultants={consultants}
        onSubmit={handleSubmit}
        onClose={handleCloseModal}
        submitting={submitting}
      />

      <DeleteConfirmModal isOpen={showDeleteModal} onClose={handleDeleteCancel} onConfirm={handleDeleteConfirm} loading={deleting}>
        {userToDelete && (
          <p style={{ margin: 0 }}>
            Voulez-vous vraiment supprimer l'utilisateur{' '}
            <strong>{userToDelete.prenom} {userToDelete.nom}</strong> ({userToDelete.email}) ?
            <br />
            <span style={{ color: colors.secondary.mediumGray, fontSize: '0.875rem' }}>Cette action est irréversible.</span>
          </p>
        )}
      </DeleteConfirmModal>

      {showPhotoModal && userForPhoto && (
        <UpdatePhotoModal
          isOpen={showPhotoModal}
          onClose={() => { setShowPhotoModal(false); setUserForPhoto(null); }}
          currentPhoto={userForPhoto.photo_url}
          onPhotoUpdate={handlePhotoUpdate}
          userName={`${userForPhoto.prenom} ${userForPhoto.nom}`}
        />
      )}
    </div>
  );
}

// --- Page principale ---

function AdminPage() {
  const [activeTab, setActiveTab] = useState('utilisateurs');
  // animating : { tab: string, dir: 'left' | 'right' } | null
  const [animating, setAnimating] = useState(null);
  // Changer cette clé force le remontage de ConsultantsTab (donc un rechargement de ses
  // données) — utilisé quand l'onglet OAuth Qonto ouvert par cette page se referme, pour
  // refléter la nouvelle connexion sans que l'utilisateur ait à rafraîchir manuellement.
  const [consultantsRefreshKey, setConsultantsRefreshKey] = useState(0);
  const { showToast } = useToast();

  // Écoute, sur TOUS les onglets de l'app ouverts (y compris l'onglet d'origine qui a lancé
  // le consentement Qonto dans un nouvel onglet), la notification de fin de connexion posée
  // par l'effet ci-dessous depuis l'onglet qui a reçu la redirection de Qonto.
  useEffect(() => {
    const channel = new BroadcastChannel('qonto-oauth');
    channel.onmessage = (event) => {
      const { status, target, message } = event.data || {};
      if (status === 'success') {
        showToast(`Connexion Qonto réussie (${target === 'gie' ? 'GIE' : 'consultant'}) — pense à lancer le script d'enregistrement du webhook`, 'success');
        setActiveTab('consultants');
        setConsultantsRefreshKey((k) => k + 1);
      } else if (status === 'error') {
        showToast(`Échec de la connexion Qonto : ${message || 'erreur inconnue'}`, 'error');
      }
    };
    return () => channel.close();
  }, [showToast]);

  // Retour du consentement OAuth Qonto (cf. api/qonto-oauth-callback.js), dans l'onglet qui a
  // reçu la redirection : diffuse le résultat aux autres onglets (dont l'onglet d'origine),
  // puis nettoie l'URL pour ne pas re-déclencher le toast à un rechargement.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qontoOAuth = params.get('qonto_oauth');
    if (!qontoOAuth) return;

    const channel = new BroadcastChannel('qonto-oauth');
    if (qontoOAuth === 'success') {
      const target = params.get('target');
      channel.postMessage({ status: 'success', target });
      showToast(`Connexion Qonto réussie (${target === 'gie' ? 'GIE' : 'consultant'})`, 'success');
    } else if (qontoOAuth === 'error') {
      const message = params.get('message');
      channel.postMessage({ status: 'error', message });
      showToast(`Échec de la connexion Qonto : ${message || 'erreur inconnue'}`, 'error');
    }
    channel.close();

    params.delete('qonto_oauth');
    params.delete('target');
    params.delete('message');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));

    // Cet onglet a été ouvert depuis l'onglet d'origine (window.open) pour le consentement
    // Qonto : le refermer automatiquement ramène le focus sur l'onglet d'origine, plutôt que
    // de laisser un second onglet ouvert avec l'app. window.close() ne fait rien (silencieusement)
    // si l'onglet n'a pas été ouvert par script — dans ce cas on reste simplement dessus.
    const closeTimer = setTimeout(() => window.close(), 2000);
    return () => clearTimeout(closeTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;
    const currentIdx = TABS.findIndex(t => t.id === activeTab);
    const newIdx     = TABS.findIndex(t => t.id === newTab);
    setAnimating({ tab: newTab, dir: newIdx > currentIdx ? 'right' : 'left' });
    setActiveTab(newTab);
  };

  const slideAnim = (tabId) => {
    if (!animating || animating.tab !== tabId) return 'none';
    return animating.dir === 'right' ? 'slideFromRight 220ms ease-out' : 'slideFromLeft 220ms ease-out';
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <style>{`
        @keyframes spin           { 0% { transform: rotate(0deg); }   100% { transform: rotate(360deg); } }
        @keyframes slideFromRight { from { transform: translateX(52px);  opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideFromLeft  { from { transform: translateX(-52px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '0 0 1.5rem 0' }}>
        <div style={{
          backgroundColor: colors.primary.gray,
          borderRadius: '0.75rem',
          padding: '0.6rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Settings size={28} color={colors.text.white} />
        </div>
        <h1 style={{ fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif', fontSize: '2rem', fontWeight: 'bold', color: colors.primary.gray, margin: 0 }}>
          Administration
        </h1>
      </div>

      <TabBar activeTab={activeTab} onTabChange={handleTabChange} />

      <AnimatedContent>
        <div style={{ display: activeTab === 'utilisateurs' ? 'block' : 'none', animation: slideAnim('utilisateurs') }}>
          <UsersTabContent />
        </div>
        <div style={{ display: activeTab === 'prestataires' ? 'block' : 'none', animation: slideAnim('prestataires') }}>
          <PrestatairesTab />
        </div>
        <div style={{ display: activeTab === 'clients' ? 'block' : 'none', animation: slideAnim('clients') }}>
          <ClientsTab />
        </div>
        <div style={{ display: activeTab === 'projets' ? 'block' : 'none', animation: slideAnim('projets') }}>
          <ProjetsTab />
        </div>
        <div style={{ display: activeTab === 'consultants' ? 'block' : 'none', animation: slideAnim('consultants') }}>
          <ConsultantsTab key={consultantsRefreshKey} />
        </div>
      </AnimatedContent>
    </div>
  );
}

export default AdminPage;
